// src/components/proposal/AIFeedbackPanel.tsx
// Comprehensive AI Feedback display panel for orchestrated proposals

import React, { useState } from 'react';
import {
  AlertCircle,
  CheckCircle,
  TrendingUp,
  Target,
  ChevronDown,
  ChevronRight,
  Star,
  Clock,
  ExternalLink,
  Lightbulb,
  FileText,
  AlertTriangle,
  ArrowRight,
} from 'lucide-react';
import { AIFeedback } from '../../services/orchestrated-proposal-service';

interface AIFeedbackPanelProps {
  feedback: AIFeedback;
  onNavigateToProfile?: (section: string) => void;
}

export function AIFeedbackPanel({ feedback, onNavigateToProfile }: AIFeedbackPanelProps) {
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['overview', 'criticalIssues']));

  const toggleSection = (section: string) => {
    setExpandedSections(prev => {
      const next = new Set(prev);
      if (next.has(section)) {
        next.delete(section);
      } else {
        next.add(section);
      }
      return next;
    });
  };

  const getScoreColor = (score: number) => {
    if (score >= 85) return 'text-success bg-success-bg';
    if (score >= 70) return 'text-bw-primary bg-bw-primary-tint';
    if (score >= 55) return 'text-fit-moderate bg-fit-moderate-bg';
    return 'text-urgent bg-urgent-bg';
  };

  const getScoreBg = (score: number) => {
    if (score >= 85) return 'bg-success-bg0';
    if (score >= 70) return 'bg-bw-primary-tint0';
    if (score >= 55) return 'bg-fit-moderate-bg0';
    return 'bg-urgent-bg0';
  };

  const getPriorityBadge = (priority: string | number) => {
    const p = typeof priority === 'number' ? priority : priority;
    if (p === 1 || p === 'high') return 'bg-urgent-bg text-urgent';
    if (p === 2 || p === 'medium') return 'bg-fit-moderate-bg text-fit-moderate';
    return 'bg-success-bg text-success';
  };

  return (
    <div className="space-y-6">
      {/* Overall Assessment */}
      <div className="bg-bw-surface rounded-xl border border-bw-border overflow-hidden">
        <button
          onClick={() => toggleSection('overview')}
          className="w-full flex items-center justify-between p-4 bg-bw-surface-subtle hover:from-slate-100 hover:to-slate-50 transition-colors"
        >
          <div className="flex items-center gap-3">
            <TrendingUp className="h-5 w-5 text-bw-primary" />
            <span className="font-semibold text-bw-ink">Overall Assessment</span>
          </div>
          {expandedSections.has('overview') ? (
            <ChevronDown className="h-5 w-5 text-bw-muted" />
          ) : (
            <ChevronRight className="h-5 w-5 text-bw-muted" />
          )}
        </button>

        {expandedSections.has('overview') && (
          <div className="p-6 border-t border-bw-border space-y-6">
            {/* Score Cards */}
            <div className="grid grid-cols-4 gap-4">
              <div className={`rounded-lg p-4 ${getScoreColor(feedback.overallAssessment.overallScore)}`}>
                <p className="text-sm font-medium opacity-80">Overall Score</p>
                <p className="text-3xl font-bold">{feedback.overallAssessment.overallScore}</p>
              </div>
              <div className="bg-bw-primary-tint rounded-lg p-4">
                <p className="text-sm text-bw-primary font-medium">Win Probability</p>
                <div className="flex items-center gap-2">
                  <p className="text-3xl font-bold text-bw-primary">{feedback.overallAssessment.winProbability}%</p>
                </div>
              </div>
              <div className="bg-bw-primary-tint rounded-lg p-4">
                <p className="text-sm text-bw-primary font-medium">Critical Issues</p>
                <p className="text-3xl font-bold text-bw-primary">{feedback.criticalIssues?.length || 0}</p>
              </div>
              <div className="bg-fit-moderate-bg rounded-lg p-4">
                <p className="text-sm text-fit-moderate font-medium">Action Items</p>
                <p className="text-3xl font-bold text-fit-moderate">{feedback.prioritizedActions?.length || 0}</p>
              </div>
            </div>

            {/* Win Probability Bar */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-bw-ink">Win Probability</span>
                <span className="text-sm text-bw-muted">{feedback.overallAssessment.winProbability}%</span>
              </div>
              <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-fit-strong-stroke rounded-full transition-all"
                  style={{ width: `${feedback.overallAssessment.winProbability}%` }}
                />
              </div>
              <p className="text-sm text-bw-muted mt-2 italic">
                {feedback.overallAssessment.winProbabilityRationale}
              </p>
            </div>

            {/* Summary */}
            <div className="bg-slate-50 rounded-lg p-4">
              <h4 className="font-medium text-bw-ink mb-2">Summary</h4>
              <p className="text-bw-ink text-sm leading-relaxed whitespace-pre-wrap">
                {feedback.overallAssessment.summary}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Critical Issues */}
      {feedback.criticalIssues && feedback.criticalIssues.length > 0 && (
        <div className="bg-bw-surface rounded-xl border border-red-200 overflow-hidden">
          <button
            onClick={() => toggleSection('criticalIssues')}
            className="w-full flex items-center justify-between p-4 bg-urgent-bg hover:bg-urgent-bg transition-colors"
          >
            <div className="flex items-center gap-3">
              <AlertTriangle className="h-5 w-5 text-urgent" />
              <span className="font-semibold text-red-900">
                Critical Issues ({feedback.criticalIssues.length})
              </span>
            </div>
            {expandedSections.has('criticalIssues') ? (
              <ChevronDown className="h-5 w-5 text-red-400" />
            ) : (
              <ChevronRight className="h-5 w-5 text-red-400" />
            )}
          </button>

          {expandedSections.has('criticalIssues') && (
            <div className="p-4 border-t border-red-100 space-y-3">
              {feedback.criticalIssues.map((issue, index) => (
                <div key={index} className="bg-bw-surface rounded-lg p-4 border border-red-100">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <p className="font-medium text-red-900">{issue.issue}</p>
                      <p className="text-sm text-urgent mt-1">{issue.recommendation}</p>
                    </div>
                    <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                      issue.impact === 'High' ? 'bg-urgent-bg text-urgent' :
                      issue.impact === 'Medium' ? 'bg-fit-moderate-bg text-fit-moderate' :
                      'bg-success-bg text-success'
                    }`}>
                      {issue.impact} Impact
                    </span>
                  </div>
                  <div className="flex items-center gap-2 mt-3">
                    <span className="text-xs text-bw-muted bg-bw-surface-subtle px-2 py-0.5 rounded">
                      {issue.section}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Prioritized Actions */}
      {feedback.prioritizedActions && feedback.prioritizedActions.length > 0 && (
        <div className="bg-bw-surface rounded-xl border border-bw-border overflow-hidden">
          <button
            onClick={() => toggleSection('actions')}
            className="w-full flex items-center justify-between p-4 bg-bw-surface-subtle hover:from-amber-100 hover:to-amber-50 transition-colors"
          >
            <div className="flex items-center gap-3">
              <Target className="h-5 w-5 text-fit-moderate" />
              <span className="font-semibold text-bw-ink">
                Prioritized Actions ({feedback.prioritizedActions.length})
              </span>
            </div>
            {expandedSections.has('actions') ? (
              <ChevronDown className="h-5 w-5 text-bw-muted" />
            ) : (
              <ChevronRight className="h-5 w-5 text-bw-muted" />
            )}
          </button>

          {expandedSections.has('actions') && (
            <div className="p-4 border-t border-bw-border space-y-3">
              {feedback.prioritizedActions.map((action, index) => (
                <div key={index} className="flex items-start gap-4 p-4 bg-slate-50 rounded-lg">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold ${getPriorityBadge(action.priority).includes('red') ? 'bg-urgent-bg0' : getPriorityBadge(action.priority).includes('yellow') ? 'bg-fit-moderate-bg0' : 'bg-success-bg0'}`}>
                    {action.priority}
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-bw-ink">{action.action}</p>
                    <p className="text-sm text-bw-muted mt-1">{action.expectedImpact}</p>
                    <div className="flex items-center gap-3 mt-2">
                      <span className="text-xs bg-bw-surface-subtle text-bw-muted px-2 py-0.5 rounded">
                        {action.section}
                      </span>
                      <span className="text-xs flex items-center gap-1 text-bw-muted">
                        <Clock className="h-3 w-3" />
                        {action.timeEstimate}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Section Feedback */}
      <div className="bg-bw-surface rounded-xl border border-bw-border overflow-hidden">
        <button
          onClick={() => toggleSection('sectionFeedback')}
          className="w-full flex items-center justify-between p-4 bg-bw-surface-subtle hover:from-slate-100 hover:to-slate-50 transition-colors"
        >
          <div className="flex items-center gap-3">
            <FileText className="h-5 w-5 text-slate-600" />
            <span className="font-semibold text-bw-ink">Section-by-Section Feedback</span>
          </div>
          {expandedSections.has('sectionFeedback') ? (
            <ChevronDown className="h-5 w-5 text-bw-muted" />
          ) : (
            <ChevronRight className="h-5 w-5 text-bw-muted" />
          )}
        </button>

        {expandedSections.has('sectionFeedback') && feedback.sectionFeedback && (
          <div className="p-4 border-t border-bw-border space-y-4">
            {Object.entries(feedback.sectionFeedback).map(([sectionName, section]) => (
              <div key={sectionName} className="border border-bw-border rounded-lg overflow-hidden">
                <div className="flex items-center justify-between p-4 bg-bw-surface-subtle">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${getScoreColor(section.score)}`}>
                      {section.score}
                    </div>
                    <span className="font-medium text-bw-ink capitalize">
                      {sectionName.replace(/([A-Z])/g, ' $1').trim()}
                    </span>
                  </div>
                  <div className="h-2 w-24 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className={`h-full ${getScoreBg(section.score)} rounded-full`}
                      style={{ width: `${section.score}%` }}
                    />
                  </div>
                </div>

                <div className="p-4 space-y-4">
                  {/* Strengths */}
                  {section.strengths && section.strengths.length > 0 && (
                    <div>
                      <h5 className="text-sm font-medium text-success mb-2 flex items-center gap-1">
                        <CheckCircle className="h-4 w-4" /> Strengths
                      </h5>
                      <ul className="space-y-1">
                        {section.strengths.map((strength, i) => (
                          <li key={i} className="text-sm text-bw-muted flex items-start gap-2">
                            <span className="text-success mt-1">+</span>
                            {strength}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Weaknesses */}
                  {section.weaknesses && section.weaknesses.length > 0 && (
                    <div>
                      <h5 className="text-sm font-medium text-urgent mb-2 flex items-center gap-1">
                        <AlertCircle className="h-4 w-4" /> Areas for Improvement
                      </h5>
                      <ul className="space-y-1">
                        {section.weaknesses.map((weakness, i) => (
                          <li key={i} className="text-sm text-bw-muted flex items-start gap-2">
                            <span className="text-urgent mt-1">-</span>
                            {weakness}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Recommendations with Examples */}
                  {section.recommendations && section.recommendations.length > 0 && (
                    <div className="border-t border-bw-border pt-4">
                      <h5 className="text-sm font-medium text-bw-primary mb-3 flex items-center gap-1">
                        <Lightbulb className="h-4 w-4" /> Recommendations
                      </h5>
                      <div className="space-y-3">
                        {section.recommendations.map((rec, i) => (
                          <div key={i} className="bg-bw-primary-tint rounded-lg p-3">
                            <p className="text-sm font-medium text-blue-900">{rec.issue}</p>
                            <p className="text-sm text-bw-primary mt-1">{rec.recommendation}</p>
                            {rec.example && (
                              <div className="mt-2 bg-bw-surface rounded p-2 border border-blue-100">
                                <p className="text-xs text-bw-muted mb-1">Example:</p>
                                <p className="text-sm text-bw-ink italic">{rec.example}</p>
                              </div>
                            )}
                            {rec.source && (
                              <p className="text-xs text-bw-primary mt-2 flex items-center gap-1">
                                <ExternalLink className="h-3 w-3" />
                                Source: {rec.source}
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Compliance Notes */}
                  {section.complianceNotes && (
                    <div className="bg-fit-moderate-bg rounded-lg p-3">
                      <h5 className="text-xs font-medium text-fit-moderate mb-1">Compliance Notes</h5>
                      <p className="text-sm text-amber-900">{section.complianceNotes}</p>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Competitive Positioning */}
      {feedback.competitivePositioning && (
        <div className="bg-bw-surface rounded-xl border border-bw-border overflow-hidden">
          <button
            onClick={() => toggleSection('competitive')}
            className="w-full flex items-center justify-between p-4 bg-bw-surface-subtle hover:from-purple-100 hover:to-purple-50 transition-colors"
          >
            <div className="flex items-center gap-3">
              <Star className="h-5 w-5 text-bw-primary" />
              <span className="font-semibold text-bw-ink">Competitive Positioning</span>
            </div>
            {expandedSections.has('competitive') ? (
              <ChevronDown className="h-5 w-5 text-bw-muted" />
            ) : (
              <ChevronRight className="h-5 w-5 text-bw-muted" />
            )}
          </button>

          {expandedSections.has('competitive') && (
            <div className="p-4 border-t border-bw-border grid grid-cols-2 gap-4">
              {/* Strengths */}
              <div className="bg-success-bg rounded-lg p-4">
                <h5 className="font-medium text-success mb-3">Competitive Strengths</h5>
                <ul className="space-y-2">
                  {feedback.competitivePositioning.strengths?.map((s, i) => (
                    <li key={i} className="text-sm text-success flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                      {s}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Differentiators */}
              <div className="bg-bw-primary-tint rounded-lg p-4">
                <h5 className="font-medium text-bw-primary mb-3">Key Differentiators</h5>
                <ul className="space-y-2">
                  {feedback.competitivePositioning.differentiators?.map((d, i) => (
                    <li key={i} className="text-sm text-bw-primary flex items-start gap-2">
                      <Star className="h-4 w-4 mt-0.5 flex-shrink-0" />
                      {d}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Weaknesses */}
              <div className="bg-fit-moderate-bg rounded-lg p-4">
                <h5 className="font-medium text-fit-moderate mb-3">Competitive Weaknesses</h5>
                <ul className="space-y-2">
                  {feedback.competitivePositioning.weaknesses?.map((w, i) => (
                    <li key={i} className="text-sm text-fit-moderate flex items-start gap-2">
                      <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                      {w}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Risks */}
              <div className="bg-urgent-bg rounded-lg p-4">
                <h5 className="font-medium text-urgent mb-3">Competitive Risks</h5>
                <ul className="space-y-2">
                  {feedback.competitivePositioning.risks?.map((r, i) => (
                    <li key={i} className="text-sm text-urgent flex items-start gap-2">
                      <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                      {r}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Resource Suggestions */}
      {feedback.resourceSuggestions && feedback.resourceSuggestions.length > 0 && (
        <div className="bg-bw-surface rounded-xl border border-bw-border overflow-hidden">
          <button
            onClick={() => toggleSection('resources')}
            className="w-full flex items-center justify-between p-4 bg-bw-surface-subtle hover:from-cyan-100 hover:to-cyan-50 transition-colors"
          >
            <div className="flex items-center gap-3">
              <ExternalLink className="h-5 w-5 text-cyan-600" />
              <span className="font-semibold text-bw-ink">
                Where to Find Missing Information ({feedback.resourceSuggestions.length})
              </span>
            </div>
            {expandedSections.has('resources') ? (
              <ChevronDown className="h-5 w-5 text-bw-muted" />
            ) : (
              <ChevronRight className="h-5 w-5 text-bw-muted" />
            )}
          </button>

          {expandedSections.has('resources') && (
            <div className="p-4 border-t border-bw-border space-y-3">
              {feedback.resourceSuggestions.map((resource, index) => (
                <div key={index} className="flex items-start gap-4 p-3 bg-cyan-50 rounded-lg">
                  <ArrowRight className="h-4 w-4 text-cyan-600 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="font-medium text-cyan-900">{resource.need}</p>
                    <p className="text-sm text-cyan-700 mt-1">
                      <span className="font-medium">Source:</span> {resource.source}
                    </p>
                    <div className="flex items-center gap-2 mt-2">
                      <span className="text-xs bg-cyan-100 text-cyan-700 px-2 py-0.5 rounded">
                        {resource.section}
                      </span>
                      <span className={`text-xs px-2 py-0.5 rounded ${
                        resource.priority === 'High' ? 'bg-urgent-bg text-urgent' :
                        resource.priority === 'Medium' ? 'bg-fit-moderate-bg text-fit-moderate' :
                        'bg-success-bg text-success'
                      }`}>
                        {resource.priority} Priority
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default AIFeedbackPanel;
